// Shared SSE client for the consolidated /api/events/stream endpoint.
//
// One EventSource per tab replaces the five per-feature streams
// (notifications, sessions, forums, reminders, threads). The server tags
// every message with a `type`; components subscribe to just the types they
// care about:
//
//   const unsubscribe = subscribeToEvents('notifications', (payload) => { ... });
//   ...
//   unsubscribe(); // in the effect cleanup
//
// Special channels:
//   '$open'  — fired when the underlying connection (re)opens
//   '$error' — fired on connection errors (EventSource auto-reconnects;
//              use this to start fallback polling if desired)
//
// The connection is opened lazily on the first subscription and closed when
// the last subscriber unsubscribes (e.g. on logout, when the dashboard
// components unmount).

let eventSource = null;
const listeners = new Map(); // type -> Set<callback>

function dispatch(type, payload) {
    const set = listeners.get(type);
    if (!set) return;
    set.forEach((callback) => {
        try {
            callback(payload);
        } catch (err) {
            console.error(`EventStream: '${type}' handler threw:`, err);
        }
    });
}

function subscriberCount() {
    let n = 0;
    listeners.forEach((set) => { n += set.size; });
    return n;
}

function openConnection() {
    if (eventSource) return;

    const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    console.log('EventStream: Opening consolidated SSE connection');
    eventSource = new EventSource(`${baseURL}/api/events/stream`, {
        withCredentials: true
    });

    eventSource.onopen = () => {
        console.log('EventStream: Connected');
        dispatch('$open');
    };

    eventSource.onmessage = (event) => {
        try {
            const data = JSON.parse(event.data);
            if (data.type === 'connected') {
                console.log('EventStream: Server confirmed connection');
                return;
            }
            dispatch(data.type, data);
        } catch (err) {
            console.error('EventStream: Error parsing message:', err, event.data);
        }
    };

    eventSource.onerror = (error) => {
        // EventSource reconnects automatically; subscribers may use '$error'
        // to start fallback polling.
        console.error('EventStream: Connection error (auto-reconnecting):', error);
        dispatch('$error', { readyState: eventSource ? eventSource.readyState : undefined });
    };
}

function closeConnectionIfIdle() {
    if (eventSource && subscriberCount() === 0) {
        console.log('EventStream: No subscribers left, closing connection');
        eventSource.close();
        eventSource = null;
    }
}

export function subscribeToEvents(type, callback) {
    if (!listeners.has(type)) {
        listeners.set(type, new Set());
    }
    listeners.get(type).add(callback);
    openConnection();

    return function unsubscribe() {
        const set = listeners.get(type);
        if (set) {
            set.delete(callback);
            if (set.size === 0) listeners.delete(type);
        }
        closeConnectionIfIdle();
    };
}
